import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import Script from 'next/script';
interface TurnstileProps {
  siteKey: string;
  onVerify?: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}
export interface TurnstileRef {
  reset: () => void;
}
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
        appearance?: 'always' | 'execute' | 'interaction-only';
      }) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}
const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(function Turnstile(t0, ref) {
  const $ = _c(16);
  const {
    siteKey,
    onVerify,
    onError,
    onExpire
  } = t0;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  let t1;
  if ($[0] !== onError || $[1] !== onExpire || $[2] !== onVerify) {
    t1 = () => {
      onVerifyRef.current = onVerify;
      onErrorRef.current = onError;
      onExpireRef.current = onExpire;
    };
    $[0] = onError;
    $[1] = onExpire;
    $[2] = onVerify;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      }
    });
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[5] !== siteKey) {
    t3 = () => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: token => {
            onVerifyRef.current?.(token);
          },
          "error-callback": () => {
            onErrorRef.current?.();
          },
          "expired-callback": () => {
            onExpireRef.current?.();
          }
        });
      }
    };
    $[5] = siteKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const renderWidget = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== siteKey) {
    t5 = [siteKey];
    $[8] = siteKey;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => onErrorRef.current?.();
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== renderWidget) {
    t7 = <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" strategy="afterInteractive" onReady={renderWidget} onError={t6} />;
    $[11] = renderWidget;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div ref={containerRef} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = <>{t7}{t8}</>;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
});
export default Turnstile;